import axios from 'axios';

/**
 * EDILCIMA API client - Auth via Bearer token in sessionStorage.
 *
 * NOTA SECURITY: il backend (core.get_current_user) supporta DUAL-MODE auth:
 *   1. Cookie httpOnly "access_token" (preferibile, immune a XSS)
 *   2. Bearer header (fallback)
 *
 * Default attuale: solo Bearer header — il proxy K8s davanti al backend
 * sovrascrive `Access-Control-Allow-Origin: *` rompendo CORS+credentials.
 *
 * Per attivare la migrazione completa a cookie httpOnly (quando il proxy
 * smettera di forzare il wildcard), basta settare la env var:
 *   REACT_APP_USE_HTTPONLY_COOKIES=true
 * e ricompilare il frontend. Il client inviera withCredentials: true
 * e smettera di leggere/scrivere il token in sessionStorage.
 *
 * sessionStorage (vs localStorage):
 * - Cancellato alla chiusura del tab → riduce esposizione XSS
 * - Non condiviso tra tab → un attacco XSS in un altro tab non legge il token qui
 */

const USE_HTTPONLY_COOKIES = process.env.REACT_APP_USE_HTTPONLY_COOKIES === 'true';

/**
 * Determina la baseURL del backend in modo robusto.
 *
 * Comportamento:
 *   1. Usa REACT_APP_BACKEND_URL se presente E coerente con l'host corrente.
 *   2. Se l'env var punta a un altro host (es: build con env di preview ma deploy
 *      su dominio di produzione), usa window.location.origin come fallback.
 *      Nell'architettura Emergent il backend è sempre /api/* sullo stesso host.
 *   3. In SSR / contesti senza window: torna alla env var (se presente).
 *
 * Questo evita il bug "build con URL preview ma servito da produzione" che
 * rompe il login per CORS / cross-origin.
 */
function resolveBackendURL() {
  const envURL = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window === 'undefined' || !window.location) {
    return envURL;
  }
  const currentOrigin = window.location.origin;
  if (!envURL) return currentOrigin;
  try {
    if (new URL(envURL).origin !== currentOrigin) {
      // eslint-disable-next-line no-console
      console.warn(
        `[EDILCIMA] REACT_APP_BACKEND_URL (${envURL}) differisce dall'host corrente (${currentOrigin}). ` +
        `Uso l'origin corrente per evitare CORS errors.`,
      );
      return currentOrigin;
    }
  } catch {
    return currentOrigin;
  }
  return envURL;
}

export const BACKEND_URL = resolveBackendURL();

const API = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: USE_HTTPONLY_COOKIES, // attiva invio cookie httpOnly se flag ON
});

const TOKEN_KEY = 'edilcima_token';

API.interceptors.request.use((config) => {
  // In modalita cookie httpOnly non leggiamo piu il token da sessionStorage:
  // l'auth e gestita interamente dal browser via Set-Cookie.
  if (USE_HTTPONLY_COOKIES) return config;
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status === 401 && !USE_HTTPONLY_COOKIES) {
      sessionStorage.removeItem(TOKEN_KEY);
    }
    return Promise.reject(err);
  },
);

export const setToken = (token) => {
  // In modalita cookie httpOnly il token è gestito dal Set-Cookie del backend; no-op qui.
  if (USE_HTTPONLY_COOKIES) return;
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

export const getToken = () => {
  if (USE_HTTPONLY_COOKIES) return null;
  return sessionStorage.getItem(TOKEN_KEY);
};

export default API;
